// src/redux/slices/blogAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { blogsService } from '../../services/blogsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchBlogsAdmin = createAsyncThunk(
    'blogAdmin/fetchBlogsAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await blogsService.getBlogs({
                ...params,
                limit: params.limit || 10,
                page: params.page || 1
            })
            return {
                blogs: response.data.blogs,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createBlog = createAsyncThunk(
    'blogAdmin/createBlog',
    async (blogData, { rejectWithValue }) => {
        try {
            const response = await blogsService.createBlog(blogData)
            return response.data.blog
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateBlog = createAsyncThunk(
    'blogAdmin/updateBlog',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await blogsService.updateBlog(id, data)
            return response.data.blog
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteBlog = createAsyncThunk(
    'blogAdmin/deleteBlog',
    async (id, { rejectWithValue }) => {
        try {
            await blogsService.deleteBlog(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchBlogCategories = createAsyncThunk(
    'blogAdmin/fetchBlogCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await blogsService.getBlogCategories()
            return response.data.categories
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const blogAdminSlice = createSlice({
    name: 'blogAdmin',
    initialState: {
        blogs: [],
        currentBlog: null,
        categories: [],
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentBlog: (state) => {
            state.currentBlog = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Blogs Admin
            .addCase(fetchBlogsAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchBlogsAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.blogs = action.payload.blogs
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchBlogsAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Create Blog
            .addCase(createBlog.pending, (state) => {
                state.loading = true
            })
            .addCase(createBlog.fulfilled, (state, action) => {
                state.loading = false
                state.blogs.unshift(action.payload)
                state.error = null
            })
            .addCase(createBlog.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Update Blog
            .addCase(updateBlog.pending, (state) => {
                state.loading = true
            })
            .addCase(updateBlog.fulfilled, (state, action) => {
                state.loading = false
                const index = state.blogs.findIndex(blog => blog._id === action.payload._id)
                if (index !== -1) {
                    state.blogs[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateBlog.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Delete Blog
            .addCase(deleteBlog.fulfilled, (state, action) => {
                state.blogs = state.blogs.filter(blog => blog._id !== action.payload)
                state.error = null
            })
            
            // Fetch Categories
            .addCase(fetchBlogCategories.fulfilled, (state, action) => {
                state.categories = action.payload
            })
    }
})

export const { clearCurrentBlog, clearError, setPagination } = blogAdminSlice.actions
export default blogAdminSlice.reducer